import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { invoiceDate } from '../../utils/dates';
import { useReactToPrint } from 'react-to-print';
import Billing from './Billing';
import BillPrint from './BillPrint';
import NoDataFound from '../Utils/NoDataFound';
import PageHeader from '../common/PageHeader';
import SearchField from '../common/SearchField';
import { useCancelBillMutation, useGetBillPageMutation, useGetShopSettingQuery, useLazyGetBillByIdQuery } from '../../api/billing/BillApi';
import { Bill } from '../../types/bill';
import { MasterRow } from '../../types/master';
import { useSnackbar } from '../../utils/snackbar';
import { can } from '../../utils/auth';
import { readable } from '../../utils/text';

const paymentLabel: Record<string, string> = {
  CASH: 'Cash',
  UPI_CARD: 'UPI / Card',
  CREDIT: 'Credit',
  EMI: 'EMI',
  SPLIT: 'Split',
};

const BillList = () => {
  const [editId, setEditId] = useState<number | null>(null);
  /** True while the counter screen is open for a brand-new bill. */
  const [creating, setCreating] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [rows, setRows] = useState<MasterRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [detail, setDetail] = useState<Bill | null>(null);
  /** The bill the printer is holding - separate, so printing opens nothing on screen. */
  const [billForPrinter, setBillForPrinter] = useState<Bill | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MasterRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const { openSnackbar } = useSnackbar();

  const [getBillPage] = useGetBillPageMutation();
  const [getBillById] = useLazyGetBillByIdQuery();
  const [cancelBill, { isLoading: cancelling }] = useCancelBillMutation();
  const { data: shop } = useGetShopSettingQuery();

  const handlePrint = useReactToPrint({ content: () => printRef.current });

  // Correcting or undoing a sale is a permission of its own; viewing and printing
  // are not.
  const mayCreate = can('BILLING', 'create');
  const canSeeList = can('BILLS', 'view');
  const mayEdit = can('BILLS', 'edit');
  const mayCancel = can('BILLS', 'delete');

  const load = (search: string, page: number, size: number) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setLoading(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const response = await getBillPage({ searchValue: search, page, size }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 600);
  };

  useEffect(() => {
    if (editId === null) load(searchValue, paginationModel.page, paginationModel.pageSize);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, paginationModel.page, paginationModel.pageSize, editId]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const openBill = async (id: number) => {
    try {
      setDetail(await getBillById(id).unwrap());
    } catch {
      openSnackbar('Could not open the bill', 'error');
    }
  };

  /**
   * Printing goes straight to the printer.
   *
   * <p>It used to load the bill into the view dialog and print from there, so the
   * shop saw the bill open on screen and then had to close it again. The hidden
   * print page has its own copy of the bill now, which is what the printer reads.
   */
  const printBill = async (id: number) => {
    try {
      setBillForPrinter(await getBillById(id).unwrap());
      // One paint, so the hidden page is rendered with this bill before printing.
      setTimeout(() => handlePrint?.(), 200);
    } catch {
      openSnackbar('Could not print the bill', 'error');
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget?.id) return;
    try {
      await cancelBill({ id: Number(cancelTarget.id), reason: cancelReason }).unwrap();
      openSnackbar('Bill cancelled and stock returned', 'success');
      load(searchValue, paginationModel.page, paginationModel.pageSize);
    } catch (error: any) {
      openSnackbar(error?.data?.message || 'Could not cancel the bill', 'error');
    } finally {
      setCancelTarget(null);
      setCancelReason('');
    }
  };

  const columns: GridColDef[] = [
    { field: 'billNo', headerName: 'Bill No', flex: 1, minWidth: 130, align: 'center', headerAlign: 'center' },
    {
      field: 'billDate',
      headerName: 'Date',
      flex: 1,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      // The backend sends 2026-10-14; a shop reads 14-10-2026.
      valueFormatter: (value) => invoiceDate(value as string),
    },
    { field: 'customerName', headerName: 'Customer', flex: 1, minWidth: 150, align: 'center', headerAlign: 'center' },
    {
      field: 'mobileNumber',
      headerName: 'Mobile',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      // Two customers share a name far more often than a number, so this is what
      // tells them apart - and it is what a shop has to hand when calling someone back.
      renderCell: (row) =>
        row.value ? (
          String(row.value)
        ) : (
          <Typography variant="caption" color="text.secondary">
            Not given
          </Typography>
        ),
    },
    {
      field: 'paymentType',
      headerName: 'Payment',
      flex: 1,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (row) => paymentLabel[String(row.value)] || String(row.value),
    },
    { field: 'totalAmount', headerName: 'Total', flex: 1, minWidth: 110, align: 'center', headerAlign: 'center' },
    { field: 'balanceAmount', headerName: 'Balance', flex: 1, minWidth: 110, align: 'center', headerAlign: 'center' },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (row) => (
        <Chip size="small" label={readable(row.value as string)} color={row.value === 'CANCELLED' ? 'error' : 'success'} variant="outlined" />
      ),
    },
    {
      field: 'Action',
      headerName: 'Action',
      flex: 1,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (row) => {
        const cancelled = row.row.status === 'CANCELLED';
        const isEmi = row.row.paymentType === 'EMI';
        return (
          <>
            <IconButton size="small" onClick={() => openBill(Number(row.row.id))} aria-label="View">
              <VisibilityIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => printBill(Number(row.row.id))} aria-label="Print">
              <PrintIcon fontSize="small" />
            </IconButton>
            {mayEdit && (
              <IconButton
                size="small"
                disabled={cancelled || isEmi}
                title={isEmi ? 'EMI bills are cancelled, not edited' : 'Edit'}
                onClick={() => setEditId(Number(row.row.id))}
                aria-label="Edit"
              >
                <EditTwoToneIcon fontSize="small" />
              </IconButton>
            )}
            {mayCancel && (
              <IconButton size="small" color="error" disabled={cancelled} onClick={() => setCancelTarget(row.row)} aria-label="Cancel">
                <BlockIcon fontSize="small" />
              </IconButton>
            )}
          </>
        );
      },
    },
  ];

  if (editId !== null) {
    return <Billing editBillId={editId} onDone={() => setEditId(null)} />;
  }

  /*
   * A counter assistant may be allowed to raise bills without being allowed to see
   * the list of them. Rather than show that person an empty grid they have no right
   * to fill, the screen opens straight onto the counter.
   */
  if (creating || (!canSeeList && mayCreate)) {
    return (
      <Billing
        onDone={() => {
          setCreating(false);
          load(searchValue, paginationModel.page, paginationModel.pageSize);
        }}
      />
    );
  }

  return (
    <Box>
      <PageHeader title="Billing" subtitle="Every bill raised. Open one to print it, correct it, or cancel it.">
        {mayCreate && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New Bill
          </Button>
        )}
      </PageHeader>

      <Box sx={{ mb: 1 }}>
        <SearchField value={searchValue} onChange={setSearchValue} placeholder={'Any words: bill number, customer or mobile...'} width={320} />
      </Box>

      {rows.length === 0 && !loading ? (
        <NoDataFound
          onCreateClick={() => (mayCreate ? setCreating(true) : load(searchValue, paginationModel.page, paginationModel.pageSize))}
          buttonName={mayCreate ? 'New Bill' : 'Refresh'}
          message={searchValue ? 'No bills matched this search' : 'No bills yet'}
        />
      ) : (
        <DataGrid
          columns={columns}
          rows={rows}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          loading={loading}
          sx={{ height: '70vh' }}
        />
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">
            Bill {detail?.billNo}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {detail?.customerName}
            {detail?.mobileNumber ? ` · ${detail.mobileNumber}` : ''} &middot; {invoiceDate(detail?.billDate)} &middot;{' '}
            {paymentLabel[detail?.paymentType || ''] || ''}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, bgcolor: '#f4f6f6' }}>
          {/* The same page that goes to the printer, shown at a readable size. */}
          <Box sx={{ transform: 'scale(0.72)', transformOrigin: 'top left', width: '139%' }}>
            {detail && <BillPrint bill={detail} shop={shop} />}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setDetail(null)}>
            Close
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => {
              setBillForPrinter(detail);
              setTimeout(() => handlePrint?.(), 200);
            }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WarningAmberRoundedIcon color="warning" />
          <Typography fontWeight={700} fontSize="1rem">
            Cancel Bill
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bill{' '}
            <Box component="span" fontWeight={700} color="text.primary">
              {String(cancelTarget?.billNo ?? '')}
            </Box>{' '}
            stays in the list as cancelled, and its phones go back into stock.
          </Typography>
          <TextField label="Reason" size="small" fullWidth value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setCancelTarget(null)}>
            Keep it
          </Button>
          <LoadingButton variant="contained" color="error" size="small" loading={cancelling} onClick={confirmCancel}>
            Cancel Bill
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Off-screen copy for the printer. */}
      <Box sx={{ position: 'absolute', left: '-10000px', top: 0 }}>
        {billForPrinter && <BillPrint ref={printRef} bill={billForPrinter} shop={shop} />}
      </Box>
    </Box>
  );
};

export default BillList;
